import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  Marker,
  useMap,
  ZoomControl,
} from "react-leaflet";
import L from "leaflet";
import { theme } from "../constants/theme";
// Importar íconos de marcas
import renaultIcon from "../../../assets/renault.png";
import nissanIcon from "../../../assets/nissan.svg";
import fotonIcon from "../../../assets/foton.svg";
import baicIcon from "../../../assets/baic.svg";
import { formatNumber } from "../utils/formatters";
import { Typography, Card } from "antd";
import "leaflet/dist/leaflet.css";
import "../../../styles/leaflet.css";

const { Title } = Typography;

// Corregir el problema de los íconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Función para obtener el nombre de la marca según el brandId
const getBrandName = (brandId) => {
  switch (parseInt(brandId)) {
    case 1:
      return "NISSAN";
    case 2:
      return "FOTON";
    case 3:
      return "RENAULT";
    case 4:
      return "BAIC";
    default:
      return "Desconocido";
  }
};

// Crear íconos personalizados para cada marca de agencia
const createBrandIcon = (brandId, mapType) => {
  // Tamaño de ícono según el modo (día/noche)
  const size = mapType === "day" ? 40 : 35;

  // Seleccionar la imagen del ícono según la marca
  let iconUrl;
  switch (parseInt(brandId)) {
    case 1: // NISSAN
      iconUrl = nissanIcon;
      break;
    case 2: // FOTON
      iconUrl = fotonIcon;
      break;
    case 3: // RENAULT
      iconUrl = renaultIcon;
      break;
    case 4: // BAIC
      iconUrl = baicIcon;
      break;
    default:
      // Usar icono por defecto si no coincide con ninguna marca
      iconUrl = renaultIcon;
  }

  // Crear el ícono personalizado
  return new L.Icon({
    iconUrl: iconUrl,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
    className: `brand-icon brand-${brandId} ${mapType}-mode`,
  });
};

// Componente personalizado para mostrar el número dentro del círculo
const CircleLabel = ({ center, value, color }) => {
  const map = useMap();
  const labelRef = useRef(null);

  useEffect(() => {
    // Crear un div personalizado para el label
    const labelIcon = L.divIcon({
      className: "circle-number-container",
      html: `<div style="color:#000000; font-weight:bold; font-size:14px; background:transparent; text-align:center;">${value}</div>`,
      iconSize: [40, 20],
      iconAnchor: [20, 10],
    });

    // Crear un marcador con el div icon
    const marker = L.marker(center, {
      icon: labelIcon,
      interactive: false,
      zIndexOffset: 1000,
    }).addTo(map);

    labelRef.current = marker;

    return () => {
      if (labelRef.current) {
        map.removeLayer(labelRef.current);
      }
    };
  }, [map, center, value, color]);

  return null;
};

// Paletas de colores para mapas diurnos y nocturnos
// Colores para el mapa diurno - Tonos más brillantes y cálidos
const dayCantonColors = [
  "#4CAF50", // Verde
  "#2196F3", // Azul
  "#FF9800", // Naranja
  "#9C27B0", // Púrpura
  "#F44336", // Rojo
  "#009688", // Verde azulado
  "#795548", // Marrón
  "#607D8B", // Gris azulado
  "#E91E63", // Rosa
  "#673AB7", // Violeta
];

// Colores para el mapa nocturno - Tonos más oscuros y fríos
const nightCantonColors = [
  "#8E24AA", // Púrpura
  "#5E35B1", // Índigo
  "#3949AB", // Azul oscuro
  "#1E88E5", // Azul
  "#039BE5", // Azul claro
  "#00ACC1", // Cian
  "#00897B", // Verde azulado
  "#43A047", // Verde
  "#7CB342", // Verde claro
  "#C0CA33", // Lima
];

const getCircleRadius = (total_vehicles, mapType = "day") => {
  const value = parseInt(total_vehicles || 0);

  // Tamaños para el mapa diurno
  if (mapType === "day") {
    if (value > 1000) return 35;
    if (value > 500) return 30;
    if (value > 100) return 25;
    if (value > 50) return 20;
    return 15;
  }
  // Tamaños para el mapa nocturno (ligeramente más grandes para compensar menor cantidad)
  else {
    if (value > 1000) return 37;
    if (value > 500) return 32;
    if (value > 100) return 27;
    if (value > 50) return 22;
    return 17; // Tamaño mínimo más grande para el modo nocturno
  }
};

// Control de pantalla completa personalizado
const FullscreenControl = ({ mapContainerId }) => {
  const map = useMap();
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Usar useCallback para la función toggleFullscreen
  const toggleFullscreen = useCallback(() => {
    const mapContainer = document.getElementById(mapContainerId);

    if (
      !document.fullscreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      // Entrar en modo pantalla completa
      if (mapContainer.requestFullscreen) {
        mapContainer.requestFullscreen();
      } else if (mapContainer.webkitRequestFullscreen) {
        mapContainer.webkitRequestFullscreen();
      } else if (mapContainer.msRequestFullscreen) {
        mapContainer.msRequestFullscreen();
      }
    } else {
      // Salir del modo pantalla completa
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }, [mapContainerId]);

  // Detectar cambios en el estado de pantalla completa
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
      );

      // Invalidar el tamaño del mapa para ajustarlo a la nueva dimensión
      setTimeout(() => {
        map.invalidateSize();
      }, 200);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, [map]);

  useEffect(() => {
    // Crear un control personalizado para el botón de pantalla completa
    const fullscreenControl = L.Control.extend({
      options: {
        position: "topleft",
      },

      onAdd: function () {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar leaflet-control fullscreen-control"
        );

        const button = L.DomUtil.create("a", "fullscreen-button", container);

        // Iconos simplificados para mejor alineación
        const enterFullscreenIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>`;
        const exitFullscreenIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>`;

        button.innerHTML = isFullscreen
          ? exitFullscreenIcon
          : enterFullscreenIcon;

        button.href = "#";
        button.title = isFullscreen
          ? "Salir de pantalla completa"
          : "Pantalla completa";
        button.setAttribute("role", "button");
        button.setAttribute(
          "aria-label",
          isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"
        );

        L.DomEvent.on(button, "click", function (e) {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          toggleFullscreen();
        });

        return container;
      },
    });

    // Añadir el control al mapa
    const control = new fullscreenControl();
    control.addTo(map);

    // Limpiar el control cuando se desmonte el componente
    return () => {
      map.removeControl(control);
    };
  }, [map, isFullscreen, toggleFullscreen, mapContainerId]);

  return null;
};

const VehicleMap = ({
  cantonStats,
  agencies = [],
  title,
  mapHeight = "500px",
  minHeight,
  showTitle = true,
  mapType = "day", // 'day' o 'night'
}) => {
  // Generar un ID único para este mapa
  const mapId = useRef(
    `map-${mapType}-${Math.random().toString(36).substr(2, 9)}`
  );
  // Seleccionar la paleta de colores según el tipo de mapa
  const cantonColors =
    mapType === "night" ? nightCantonColors : dayCantonColors;

  // Verificar si tenemos datos para mostrar
  const hasData =
    cantonStats &&
    Object.values(cantonStats).some((cantons) => cantons && cantons.length > 0);

  if (!hasData) {
    console.warn("No hay datos para mostrar en el mapa");
  }

  return (
    <>
      {showTitle && (
        <Card
          style={{
            ...theme.cards.base,
            padding: 0,
            marginBottom: "24px",
            height: "100%",
          }}
          bodyStyle={{ padding: 0, height: "100%" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px 24px",
              height: "64px",
            }}
          >
            <Title
              level={4}
              style={{
                margin: 0,
                color: theme.colors.text,
                fontSize: "18px",
                textAlign: "center",
                width: "100%",
              }}
            >
              {title || "MAPA DE CONCENTRACIÓN DE VEHÍCULOS"}
            </Title>
          </div>
        </Card>
      )}
      <div
        id={mapId.current}
        style={{
          ...theme.map.container,
          height: mapHeight,
          minHeight: minHeight ? `${minHeight}px` : undefined,
        }}
      >
        <MapContainer
          center={[-1.5, -79.2]}
          zoom={7}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
        >
          <ZoomControl position="topleft" />
          <FullscreenControl mapContainerId={mapId.current} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Círculos de concentración con números */}
          {hasData &&
            Object.entries(cantonStats).map(
              ([province, cantons]) =>
                cantons &&
                cantons.map((canton, index) => {
                  // Verificamos que el cantón tenga datos
                  if (!canton) return null;

                  // Usar los campos apropiados según el tipo de mapa
                  // En mapa diurno usamos day_*, en mapa nocturno usamos night_*
                  let cantonName, lat, lng;

                  if (mapType === "day") {
                    cantonName = canton.day_canton;
                    lat = canton.day_lat;
                    lng = canton.day_lng;
                  } else {
                    cantonName = canton.night_canton;
                    lat = canton.night_lat;
                    lng = canton.night_lng;
                  }

                  // Si no hay coordenadas, no renderizamos este cantón
                  if (!lat || !lng || !cantonName) return null;

                  const color = cantonColors[index % cantonColors.length];
                  const center = [parseFloat(lat), parseFloat(lng)];

                  return (
                    <React.Fragment key={`${cantonName}-${lat}-${lng}`}>
                      <CircleMarker
                        center={center}
                        radius={getCircleRadius(canton.total_vehicles, mapType)}
                        fillColor={color}
                        color={color}
                        weight={2}
                        opacity={0.9}
                        fillOpacity={0.4}
                      >
                        <Popup>
                          <div style={{ padding: "12px", textAlign: "center" }}>
                            <h3
                              style={{
                                margin: "0 0 8px 0",
                                color: color,
                                fontSize: "16px",
                              }}
                            >
                              {cantonName}
                            </h3>
                            <p
                              style={{
                                margin: "0",
                                fontSize: "14px",
                                color: theme.text.secondary,
                              }}
                            >
                              <strong>
                                {formatNumber(canton.total_vehicles)}
                              </strong>{" "}
                              vehículos
                            </p>
                          </div>
                        </Popup>
                      </CircleMarker>
                      <CircleLabel
                        center={center}
                        value={formatNumber(canton.total_vehicles)}
                        color={color}
                      />
                    </React.Fragment>
                  );
                })
            )}

          {/* Marcadores de agencias */}
          {agencies &&
            agencies.map((agency) => {
              if (!agency.latitude || !agency.longitude) return null;

              // Obtener brandId desde la agencia o su relación brandDealer
              const brandId =
                agency.brandId ||
                (agency.brandDealer ? agency.brandDealer.id : null) ||
                0;

              return (
                <Marker
                  key={`agency-${agency.id}`}
                  position={[
                    parseFloat(agency.latitude),
                    parseFloat(agency.longitude),
                  ]}
                  icon={createBrandIcon(brandId, mapType)}
                >
                  <Popup>
                    <div style={{ padding: "12px", textAlign: "center" }}>
                      <h3
                        style={{
                          margin: "0 0 8px 0",
                          color: theme.colors.primary,
                          fontWeight: "600",
                          fontSize: "16px",
                        }}
                      >
                        {agency.name}
                      </h3>
                      <p
                        style={{
                          margin: "0",
                          fontSize: "14px",
                          color: theme.text.secondary,
                        }}
                      >
                        <strong>Marca:</strong> {getBrandName(brandId)}
                      </p>
                      <p
                        style={{
                          margin: "0",
                          fontSize: "14px",
                          color: theme.text.secondary,
                        }}
                      >
                        <strong>Dirección:</strong> {agency.address}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
        </MapContainer>
      </div>
    </>
  );
};

export default VehicleMap;
